import { useState, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import type { Route } from "../../../../../../store/admin/routesStore";
import type { DenseRouteCoordinate } from "../../../../../../store/admin/routesStore";
import type { RoutePoint, RouteFormValues, RouteCoordinate } from "../types";
import { LINEAR_BUFFER_DEFAULT } from "../types";

const DEFAULT_POINTS: RoutePoint[] = [
  { latitude: 19.4326, longitude: -99.1332, address: null, geocoding: false },
  { latitude: 19.44,   longitude: -99.14,   address: null, geocoding: false },
];

const DEFAULT_VALUES: RouteFormValues = {
  name: "",
  description: "",
  status_id: "",
  principal_route: [],
  principal_route_distance: 0,
  principal_route_duration: 0,
  principal_route_buffer: LINEAR_BUFFER_DEFAULT,
  first_point_radius: 500,
  last_point_radius: 500,
  deviationAlertsEnabled: true,
};

const AVG_SPEED_KMH = 50;

/** Reverse-geocode a single lat/lng via the Google Geocoder API. */
async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  if (typeof google === "undefined" || !google.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ location: { lat, lng } });
    if (result.results && result.results.length > 0) {
      return result.results[0].formatted_address;
    }
    return null;
  } catch {
    return null;
  }
}

/** Forward-geocode an address string → { lat, lng, formatted }. */
async function forwardGeocode(
  address: string
): Promise<{ lat: number; lng: number; formatted: string } | null> {
  if (typeof google === "undefined" || !google.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ address });
    if (result.results && result.results.length > 0) {
      const loc = result.results[0].geometry.location;
      return {
        lat: loc.lat(),
        lng: loc.lng(),
        formatted: result.results[0].formatted_address,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function calcEstimatedTime(distanceKm: number): string {
  const minutes = Math.round((distanceKm / AVG_SPEED_KMH) * 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

interface UseRouteFormProps {
  isEdit: boolean;
  editRoute: Route | null;
}

export function useRouteForm({ isEdit, editRoute }: UseRouteFormProps) {
  const [routePoints, setRoutePoints] = useState<RoutePoint[]>(DEFAULT_POINTS);
  const [routeDistance, setRouteDistance] = useState<number>(0);
  const [estimatedTime, setEstimatedTime] = useState<string>("");
  const [denseAxis, setDenseAxis] = useState<DenseRouteCoordinate[]>([]);

  /** Prevent stale-closure geocoding from overwriting newer state. */
  const geocodeGenRef = useRef<Record<number, number>>({});

  const form = useForm<RouteFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: "onChange",
  });
  const { watch, setValue, reset } = form;

  // ─── Reverse geocoding ────────────────────────────────────────────────────

  const geocodePoint = useCallback(async (index: number, lat: number, lng: number) => {
    const gen = (geocodeGenRef.current[index] ?? 0) + 1;
    geocodeGenRef.current[index] = gen;

    setRoutePoints((prev) =>
      prev.map((p, i) => (i === index ? { ...p, geocoding: true } : p))
    );

    const address = await reverseGeocode(lat, lng);

    setRoutePoints((prev) => {
      if (geocodeGenRef.current[index] !== gen) return prev;
      return prev.map((p, i) =>
        i === index ? { ...p, address: address, geocoding: false } : p
      );
    });
  }, []);

  const geocodeAll = useCallback(
    (points: RoutePoint[]) => {
      points.forEach((p, i) => {
        geocodePoint(i, p.latitude, p.longitude);
      });
    },
    [geocodePoint]
  );

  // ─── Sync routePoints → form field ───────────────────────────────────────

  const syncFormField = useCallback(
    (points: RoutePoint[]) => {
      setValue(
        "principal_route",
        points.map((p) => ({ latitud: p.latitude, longitud: p.longitude }))
      );
    },
    [setValue]
  );

  // ─── Public: called when map markers are dragged ──────────────────────────

  const handleMapPointsChange = useCallback(
    (newMapPoints: { lat: number; lng: number }[]) => {
      setRoutePoints((prev) => {
        const updated: RoutePoint[] = newMapPoints.map((mp, i) => {
          const existing = prev[i];
          const coordChanged =
            !existing ||
            existing.latitude !== mp.lat ||
            existing.longitude !== mp.lng;
          return {
            latitude: mp.lat,
            longitude: mp.lng,
            address: coordChanged ? null : (existing?.address ?? null),
            geocoding: coordChanged,
          };
        });
        syncFormField(updated);
        return updated;
      });

      newMapPoints.forEach((mp, i) => {
        const existing = routePoints[i];
        if (
          !existing ||
          existing.latitude !== mp.lat ||
          existing.longitude !== mp.lng
        ) {
          geocodePoint(i, mp.lat, mp.lng);
        }
      });
    },
    [routePoints, geocodePoint, syncFormField]
  );

  // ─── Public: called when a coordinate is edited in the table ─────────────

  const handleCoordinateEdit = useCallback(
    (index: number, field: "latitude" | "longitude", raw: string) => {
      const value = parseFloat(raw);
      if (isNaN(value)) return;

      let lat = value;
      let lng = value;

      setRoutePoints((prev) => {
        const pt = prev[index];
        if (!pt) return prev;
        lat = field === "latitude" ? value : pt.latitude;
        lng = field === "longitude" ? value : pt.longitude;
        const updated = prev.map((p, i) =>
          i === index
            ? { ...p, latitude: lat, longitude: lng, address: null, geocoding: true }
            : p
        );
        syncFormField(updated);
        return updated;
      });

      geocodePoint(index, lat, lng);
    },
    [geocodePoint, syncFormField]
  );

  // ─── Public: called when address is edited in the table ──────────────────

  const handleAddressEdit = useCallback(
    async (index: number, rawAddress: string) => {
      const trimmed = rawAddress.trim();
      if (!trimmed) return;

      const gen = (geocodeGenRef.current[index] ?? 0) + 1;
      geocodeGenRef.current[index] = gen;

      setRoutePoints((prev) =>
        prev.map((p, i) =>
          i === index ? { ...p, address: trimmed, geocoding: true } : p
        )
      );

      const result = await forwardGeocode(trimmed);

      if (geocodeGenRef.current[index] !== gen) return;

      if (!result) {
        setRoutePoints((prev) =>
          prev.map((p, i) =>
            i === index ? { ...p, geocoding: false } : p
          )
        );
        return;
      }

      setRoutePoints((prev) => {
        const updated = prev.map((p, i) =>
          i === index
            ? {
                ...p,
                latitude: result.lat,
                longitude: result.lng,
                address: result.formatted,
                geocoding: false,
              }
            : p
        );
        syncFormField(updated);
        return updated;
      });
    },
    [syncFormField]
  );

  // ─── Public: add / remove points ─────────────────────────────────────────

  const handleAddPoint = useCallback(() => {
    setRoutePoints((prev) => {
      const last = prev[prev.length - 1];
      const newPoint: RoutePoint = {
        latitude: last ? last.latitude + 0.005 : 19.4326,
        longitude: last ? last.longitude + 0.005 : -99.1332,
        address: null,
        geocoding: false,
      };
      const updated = [...prev, newPoint];
      syncFormField(updated);
      geocodePoint(updated.length - 1, newPoint.latitude, newPoint.longitude);
      return updated;
    });
  }, [geocodePoint, syncFormField]);

  const handleRemovePoint = useCallback(
    (index: number) => {
      setRoutePoints((prev) => {
        if (prev.length <= 2) return prev;
        const updated = prev.filter((_, i) => i !== index);
        syncFormField(updated);
        return updated;
      });
    },
    [syncFormField]
  );

  // ─── Public: route distance from Directions API ───────────────────────────

  const handleRouteCalculated = useCallback(
    (distance: number) => {
      setRouteDistance(distance);
      setEstimatedTime(calcEstimatedTime(distance));
      setValue("principal_route_distance", distance);
      setValue(
        "principal_route_duration",
        Math.round((distance / AVG_SPEED_KMH) * 60)
      );
    },
    [setValue]
  );

  const handleAxisCalculated = useCallback(
    (path: { lat: number; lng: number }[]) => {
      setDenseAxis(path.map((p) => ({ latitude: p.lat, longitude: p.lng })));
    },
    []
  );

  const handleBufferChange = useCallback(
    (value: number) => {
      setValue("principal_route_buffer", value, { shouldDirty: true });
    },
    [setValue]
  );

  // ─── Change detection ─────────────────────────────────────────────────────

  const hasRealChanges = useCallback((): boolean => {
    if (!isEdit || !editRoute) return false;

    const currentValues = watch();

    const basicChanged =
      currentValues.name !== editRoute.name ||
      currentValues.description !== editRoute.description ||
      currentValues.status_id !== editRoute.status_id;

    const compareRoute = (
      current: RouteCoordinate[],
      original: RouteCoordinate[]
    ) => {
      if (current.length !== original.length) return false;
      return current.every(
        (p, i) =>
          p.latitud === original[i].latitud &&
          p.longitud === original[i].longitud
      );
    };

    const routeChanged = !compareRoute(
      currentValues.principal_route,
      editRoute.principal_route
    );

    const radiusChanged =
      Number(currentValues.first_point_radius) !== Number(editRoute.first_point_radius ?? 500) ||
      Number(currentValues.last_point_radius) !== Number(editRoute.last_point_radius ?? 500);

    const deviationChanged =
      currentValues.deviationAlertsEnabled !== (editRoute.deviationAlertsEnabled !== false);

    const bufferChanged =
      Number(currentValues.principal_route_buffer) !==
      Number(editRoute.principal_route_buffer ?? LINEAR_BUFFER_DEFAULT);

    return basicChanged || routeChanged || radiusChanged || deviationChanged || bufferChanged;
  }, [isEdit, editRoute, watch]);

  // ─── Reset ────────────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setRoutePoints(DEFAULT_POINTS);
    setRouteDistance(0);
    setEstimatedTime("");
    setDenseAxis([]);
    geocodeGenRef.current = {};
    reset(DEFAULT_VALUES);
  }, [reset]);

  // ─── Prepare submit payload ───────────────────────────────────────────────

  const prepareRouteData = useCallback(
    (data: RouteFormValues) => ({
      name: data.name,
      description: data.description,
      status_id: data.status_id,
      geofences_id: [],
      principal_route: routePoints.map((p) => ({
        latitud: p.latitude,
        longitud: p.longitude,
      })),
      principal_route_distance: routeDistance,
      principal_route_duration: data.principal_route_duration,
      principal_route_buffer:
        Number(data.principal_route_buffer) || LINEAR_BUFFER_DEFAULT,
      first_point_radius: data.first_point_radius,
      last_point_radius: data.last_point_radius,
      deviationAlertsEnabled: data.deviationAlertsEnabled,
      ...(denseAxis.length >= 2 ? { principal_route_dense: denseAxis } : {}),
    }),
    [routePoints, routeDistance, denseAxis]
  );

  // ─── Load edit data ───────────────────────────────────────────────────────

  useEffect(() => {
    if (!isEdit || !editRoute) return;

    setValue("name", editRoute.name);
    setValue("description", editRoute.description);
    setValue("status_id", editRoute.status_id);
    setValue("deviationAlertsEnabled", editRoute.deviationAlertsEnabled !== false);

    const source =
      editRoute.principal_route && editRoute.principal_route.length >= 2
        ? editRoute.principal_route
        : [];

    setValue("first_point_radius", editRoute.first_point_radius ?? 500);
    setValue("last_point_radius", editRoute.last_point_radius ?? 500);
    setValue(
      "principal_route_buffer",
      editRoute.principal_route_buffer ?? LINEAR_BUFFER_DEFAULT
    );
    setDenseAxis(
      Array.isArray(editRoute.principal_route_dense)
        ? editRoute.principal_route_dense
        : []
    );

    if (source.length >= 2) {
      const loaded: RoutePoint[] = source.map((p) => ({
        latitude: p.latitud,
        longitude: p.longitud,
        address: null,
        geocoding: false,
      }));
      setRoutePoints(loaded);
      setValue("principal_route", source);
      geocodeAll(loaded);
    }

    if (editRoute.principal_route_distance) {
      const dist = editRoute.principal_route_distance;
      setRouteDistance(dist);
      setEstimatedTime(calcEstimatedTime(dist));
      setValue("principal_route_distance", dist);
    }
    if (editRoute.principal_route_duration) {
      setValue("principal_route_duration", editRoute.principal_route_duration);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editRoute]);

  return {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  };
}
